/** Below this foot-path distance (meters), a POI is considered reachable on foot. */
export const BEST_VEHICLE_FOOT_MAX_M = 500;
/** Below this bike-path distance (meters), a POI is considered reachable by bike (once foot is ruled out). */
export const BEST_VEHICLE_BIKE_MAX_M = 2000;

export interface PoiVehicleDistances {
    foot_path_length_m?: number | null;
    bike_path_length_m?: number | null;
}

/** Picks the most suitable vehicle for a POI when no explicit vehicle is selected ("all"). */
export function getBestVehicleForPoi(properties: PoiVehicleDistances): "foot" | "bike" | "car" {
    if ((properties.foot_path_length_m ?? Number.POSITIVE_INFINITY) < BEST_VEHICLE_FOOT_MAX_M) return "foot";
    if ((properties.bike_path_length_m ?? Number.POSITIVE_INFINITY) < BEST_VEHICLE_BIKE_MAX_M) return "bike";
    return "car";
}

export type PoiVehicle = "foot" | "bike" | "car";

/** Resolves the vehicle to rank/display a POI by: the explicit selection, or its own best-suited vehicle for "all". */
export function resolveVehicleForPoi(vehicle: PoiVehicle | "all", properties: PoiVehicleDistances): PoiVehicle {
    return vehicle === "all" ? getBestVehicleForPoi(properties) : vehicle;
}

export interface PoiRankingProperties extends PoiVehicleDistances {
    type_en?: string | null;
    name?: string | null;
    car_duration_min?: number | null;
    car_path_length_m?: number | null;
    bike_duration_min?: number | null;
    bike_path_length_m?: number | null;
    foot_duration_min?: number | null;
    foot_path_length_m?: number | null;
}

export interface PoiFeatureLike<P extends PoiRankingProperties = PoiRankingProperties> {
    properties: P;
}

/**
 * Reduces a POI feature set to at most `maxRank` per type, ranked by travel time, then
 * distance, then name. For "all", each POI is ranked by its own best-suited vehicle (see
 * `getBestVehicleForPoi`) - the grouping key is the type alone, so two POIs of the same
 * type that happen to prefer different vehicles still share one limit instead of each
 * getting their own.
 */
export function limitPoisPerType<F extends PoiFeatureLike>(
    features: F[],
    vehicle: PoiVehicle | "all",
    maxRank: number,
): F[] {
    const vehicleOf = (feature: F) => resolveVehicleForPoi(vehicle, feature.properties);

    const groups = new Map<string, F[]>();
    for (const feature of features) {
        const key = feature.properties.type_en ?? "";
        const group = groups.get(key);
        if (group) group.push(feature);
        else groups.set(key, [feature]);
    }

    const num = (feature: F, key: string) => {
        const v = (feature.properties as Record<string, unknown>)[key];
        return typeof v === "number" && !Number.isNaN(v) ? v : Number.POSITIVE_INFINITY;
    };
    const compare = (a: F, b: F) => {
        const vehicleA = vehicleOf(a),
            vehicleB = vehicleOf(b);
        const durationDiff = num(a, `${vehicleA}_duration_min`) - num(b, `${vehicleB}_duration_min`);
        if (durationDiff !== 0) return durationDiff;
        const pathDiff = num(a, `${vehicleA}_path_length_m`) - num(b, `${vehicleB}_path_length_m`);
        if (pathDiff !== 0) return pathDiff;
        const nameA = a.properties.name,
            nameB = b.properties.name;
        if (!!nameA !== !!nameB) return nameA ? -1 : 1;
        return nameA && nameB ? nameA.localeCompare(nameB) : 0;
    };

    const limited: F[] = [];
    for (const group of groups.values()) {
        group.sort(compare);
        limited.push(...group.slice(0, maxRank));
    }
    return limited;
}

export const FALLBACK_ICON_FILENAME = "coordinates.svg";

/**
 * Maps a POI category string (as returned by the location-info API) to its icon filename under assets/images.
 * Some types carry two spellings, because AVM LIVE delivers the corrected type_en only weeks after STAGE (#19887):
 * aeroport/airport, pharmacie/pharmacy, school with unknown isced-level/other school, leisure/entertainment,
 * fließende/stehende gewässer vs. flowing/standing water.
 */
export const POI_CATEGORY_TO_ICON_FILENAME: Record<string, string> = {
    "railway station": "railway_stop.svg",
    "subway station": "tram_stop.svg",
    "tram stop": "tram_stop.svg",
    aeroport: "airport.svg",
    airport: "airport.svg",
    "light rail station": "tram_stop.svg",
    "mainline station": "railway_stop.svg",
    "motorway link": "motorway_link.svg",
    "motorway link (single-digit)": "motorway_link.svg",
    "motorway link (two-digit)": "motorway_link.svg",
    "motorway link (three-digit)": "motorway_link.svg",
    "federal highway junction": "highway_junction.svg",

    hospital: "hospital.svg",
    doctor: "doctors.svg",
    pharmacie: "pharmacy.svg",
    pharmacy: "pharmacy.svg",
    kindergarten: "kindergarten.svg",
    "bus stop": "bus_stop.svg",
    "school with unknown isced-level": "kindergarten.svg",
    "other school": "kindergarten.svg",
    university: "kindergarten.svg",
    "primary school": "kindergarten.svg",
    "secondary school": "kindergarten.svg",
    "mixed school": "kindergarten.svg",

    supermarket: "supermarket.svg",
    mall: "mall.svg",
    shop: "mall.svg",
    "bakery/butchery": "bakery.svg",
    bank: "bank.svg",
    "community centre": "theater.svg",
    leisure: "cafe.svg",
    entertainment: "cafe.svg",

    administrative: "scale.svg",
    courthouse: "scale.svg",
    embassy: "embassy.svg",
    "fire station": "firefighter.svg",
    map_marker: "map_marker.svg",
    package_delivery: "package_delivery.svg",
    park: "park.svg",
    playground: "playground.svg",
    "police station": "police.svg",
    "post office": "post_office.svg",
    prison: "prison.svg",
    gastronomy: "restaurant.svg",
    "other cultural institutions": "theater.svg",
    "place of worship": "theater.svg",
    waterway: "waterway.svg",
    "fließende gewässer": "waterway.svg",
    "stehende gewässer": "waterway.svg",
    "flowing water": "waterway.svg",
    "standing water": "waterway.svg",
    agriculture: "agriculture.svg",
    forest: "forest.svg",
    "social facility": "social_institution.svg",
    sport: "sport.svg",
    municipal_center: "center.svg",
};
